// Zone effect: a subtle animated force-field ring around the player when they
// are inside a beneficial galaxy zone (healing or dash recharge). Inspired by
// canvasui.dev's Force Field component, implemented natively as a shader quad.

import * as THREE from 'three';
import type { Vec2 } from '../types.js';

export class ZoneEffect {
  readonly mesh: THREE.Mesh;
  private readonly material: THREE.ShaderMaterial;

  constructor() {
    const geometry = new THREE.PlaneGeometry(1, 1);
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new THREE.Color(0x3dffc4) },
        uStrength: { value: 0 },
      },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform float uTime;
        uniform vec3 uColor;
        uniform float uStrength;
        varying vec2 vUv;

        float hexDist(vec2 p) {
          p = abs(p);
          return max(dot(p, vec2(0.5, 0.8660254)), p.x);
        }

        void main() {
          vec2 p = vUv * 2.0 - 1.0;
          float ang = uTime * 0.8;
          float c = cos(ang);
          float s = sin(ang);
          p = mat2(c, -s, s, c) * p;

          float hd = hexDist(p);
          float rad = length(p);

          // Pulsing hex ring.
          float pulse = 0.75 + 0.15 * sin(uTime * 4.0);
          float ring = 1.0 - smoothstep(0.0, 0.06, abs(hd - pulse));

          // Six spokes.
          float th = atan(p.y, p.x);
          float spokeLine = abs(sin(th * 3.0)) * max(hd, 0.08);
          float spokes = (1.0 - smoothstep(0.0, 0.03, spokeLine)) * step(0.15, hd) * step(hd, pulse);

          // Soft inner glow.
          float glow = exp(-6.0 * rad * rad) * 0.4;

          float b = ring * 0.9 + spokes * 0.5 + glow;
          b *= uStrength;
          b *= 1.0 - smoothstep(0.85, 1.0, rad);
          if (b <= 0.004) discard;
          gl_FragColor = vec4(uColor * b, b * 0.85);
        }
      `,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    this.mesh = new THREE.Mesh(geometry, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 2;
    this.mesh.visible = false;
    this.mesh.scale.set(2.2, 2.2, 1);
  }

  update(playerPos: Vec2, inHealingZone: boolean, inDashZone: boolean, time: number): void {
    if (!inHealingZone && !inDashZone) {
      this.mesh.visible = false;
      this.material.uniforms.uStrength.value = 0;
      return;
    }
    this.mesh.visible = true;
    this.mesh.position.set(playerPos.x, playerPos.y, 0.05);
    this.material.uniforms.uTime.value = time;
    this.material.uniforms.uStrength.value = 1;
    const color = this.material.uniforms.uColor.value as THREE.Color;
    if (inHealingZone) {
      color.setHex(0x3dffc4); // green for healing
    } else {
      color.setHex(0xffd36b); // gold for dash recharge
    }
  }
}
